import {useState} from 'react'
import {ConfirmationDialog} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useArchiveDialog, ARCHIVE_STATUS} from '../contexts/ArchiveDialogContext'
import {useArchiveTask} from '../hooks/use-archive-task'
import {useClosePullRequest} from '../hooks/use-close-pull-request'

export function ArchiveConfirmationDialog() {
  const {dialogState, closeArchiveDialog, setArchiveStatus} = useArchiveDialog()
  const {mutateAsync: archiveTaskAsync, isPending: isArchiving} = useArchiveTask({
    repoOwner: dialogState.repoOwner,
    repoName: dialogState.repoName,
    taskId: dialogState.taskId ?? undefined,
  })
  const {mutateAsync: closePRAsync, isPending: isClosingPR} = useClosePullRequest({
    repoNwo: dialogState.pull?.repository_nwo,
    prNumber: dialogState.pull?.number,
  })
  const [pendingAction, setPendingAction] = useState<'archive' | 'archiveAndClose' | null>(null)
  const isOperationInProgress = isArchiving || isClosingPR

  const handleClose = () => {
    setPendingAction(null)
    closeArchiveDialog()
  }

  const hasOpenPR = dialogState.pull?.state === 'open'

  const handleArchiveOnly = async () => {
    setPendingAction('archive')
    try {
      await archiveTaskAsync()
      setArchiveStatus(ARCHIVE_STATUS.SUCCESS)
    } catch {
      setArchiveStatus(ARCHIVE_STATUS.ERROR)
    }
    handleClose()
  }

  const handleArchiveAndClosePR = async () => {
    setPendingAction('archiveAndClose')
    try {
      // Archive first to avoid race condition with backend listener that
      // auto-archives tasks when their PR is closed.
      await archiveTaskAsync()
      try {
        await closePRAsync()
        setArchiveStatus(ARCHIVE_STATUS.SUCCESS_WITH_PR_CLOSED)
      } catch {
        setArchiveStatus(ARCHIVE_STATUS.ERROR_PR_CLOSE_FAILED)
      }
    } catch {
      setArchiveStatus(ARCHIVE_STATUS.ERROR)
    }
    handleClose()
  }

  const handleSimpleClose = (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
    if (gesture === 'confirm') {
      handleArchiveOnly()
    } else {
      handleClose()
    }
  }

  if (!dialogState.isOpen) {
    return null
  }

  if (hasOpenPR) {
    return (
      <Dialog
        title="Archive session"
        width="large"
        onClose={handleClose}
        footerButtons={[
          {
            buttonType: 'normal',
            content: 'Cancel',
            onClick: handleClose,
            disabled: isOperationInProgress,
          },
          {
            buttonType: 'normal',
            content: 'Archive only',
            onClick: handleArchiveOnly,
            disabled: isOperationInProgress,
            loading: pendingAction === 'archive' && isArchiving,
          },
          {
            buttonType: 'normal',
            content: 'Archive and close',
            onClick: handleArchiveAndClosePR,
            disabled: isOperationInProgress,
            loading: pendingAction === 'archiveAndClose' && isOperationInProgress,
          },
        ]}
      >
        This session has an open pull request. Would you like to close it?
      </Dialog>
    )
  }

  return (
    <ConfirmationDialog
      onClose={handleSimpleClose}
      title="Archive session"
      confirmButtonContent="Yes, archive"
      cancelButtonContent="Cancel"
      confirmButtonType="danger"
    >
      Are you sure you want to archive this session?
    </ConfirmationDialog>
  )
}
